// The in-app "how it works" guide — plain-English, idiot-proof onboarding.
// Opened from the top-bar "How-to" button; also linked from the empty calendar.

const STEPS = [
  {
    n: 1,
    title: "Connect your accounts",
    body: "Open the Connections page from the sidebar and click Connect next to each platform — YouTube, Instagram, X, TikTok, Facebook. Each workspace holds one account per platform; add more brands as workspaces on Pro or Agency.",
    tips: [
      "Facebook asks which Page to publish to — pick one.",
      "Connect as many platforms as you actually publish to — you can skip any you don't use.",
    ],
  },
  {
    n: 2,
    title: "Make a post",
    body: 'Click "Create post", drag in your video, and write your message once. Broadside works in three stages — Create → Tailor → Review: it writes a native caption and title for each platform, and you review and edit every version before it goes out.',
    tips: [
      'Got a whole batch? Use "Import folder" to load many videos at once.',
      "You see a preview of the exact video before anything publishes.",
    ],
  },
  {
    n: 3,
    title: "Choose where and when",
    body: "Tick the platforms you want, set the privacy, then either Publish now or Schedule it for a specific time. That's it — one click sends it everywhere.",
    tips: ["If one platform can't post, only that one is skipped — the rest still go out."],
  },
  {
    n: 4,
    title: "Watch it happen",
    body: "The Dashboard is your home base — a week view of what's going out, a Today rail, and anything that needs you. Click any published post for its delivery receipt: each platform marked Published, Held, or Failed, with a link straight to the live post. A failed leg shows the reason in plain English; one that's simply on hold — say, waiting for a platform's daily limit to reset — shows a calm grey, not a failure.",
    tips: [
      "Scheduled posts fire on their own at the set time — you don't have to be here.",
      "Changed your mind? Click a scheduled post to edit or cancel it any time before it fires.",
      "The Performance board ranks your recent posts, and Analytics shows which platform each one did best on.",
      "The bell (top right) collects everything worth knowing — a failed post, an account that needs reconnecting, low credits.",
    ],
  },
];

const GOTCHAS = [
  "Publishing spends credits per platform, and the exact cost for each platform you've selected shows before you hit publish. Pick a plan or top up under Settings → Billing, where your balance always shows.",
  "TikTok requires you to choose who can view each video (Everyone, Friends, or Only me) before it publishes — that's TikTok's own rule, set it in the TikTok options.",
  "YouTube has a daily upload limit shared across the app, so a very large batch may not all go out the same day.",
];

export function Guide({ onConnect }: { onConnect: () => void }) {
  return (
    <div className="guide">
      <p className="guide-intro">
        Broadside turns one upload into a post on every platform you connect — tailored for
        each. Here's the whole thing in four steps.
      </p>

      <ol className="guide-steps">
        {STEPS.map((s) => (
          <li key={s.n} className="guide-step">
            <span className="guide-n">{s.n}</span>
            <div className="guide-step-body">
              <h3>{s.title}</h3>
              <p>{s.body}</p>
              {s.tips.length > 0 && (
                <ul className="guide-tips">
                  {s.tips.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              )}
            </div>
          </li>
        ))}
      </ol>

      <div className="guide-cta">
        <button type="button" className="btn primary" onClick={onConnect}>
          Connect an account to start
        </button>
      </div>

      <div className="card guide-gotchas">
        <h3>Good to know</h3>
        <ul>
          {GOTCHAS.map((g) => (
            <li key={g}>{g}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
